// Deep specialised content for the 7 AETHERIS pillars (RO + EN)
// Used by SiteSolutii, SitePlatforma and pillar detail sections.

import pillarCity from '../assets/pillar-city.jpg';
import pillarBuilding from '../assets/pillar-building.jpg';
import pillarCare from '../assets/pillar-care.jpg';
import pillarMind from '../assets/pillar-mind.jpg';
import pillarSecure from '../assets/pillar-secure.jpg';
import pillarIntelligence from '../assets/pillar-intelligence.jpg';
import pillarFinance from '../assets/pillar-finance.jpg';

import {
  Network, Building2, HeartPulse, Brain, ShieldCheck, Cpu, Wallet,
  type LucideIcon,
} from 'lucide-react';

export interface PillarKPI { v: string; l: string; }
export interface PillarLocaleContent {
  tag: string;
  title: string;
  headline: string;
  subtitle: string;
  capabilities: { t: string; d: string }[];
  modules: string[];
  kpis: PillarKPI[];
  useCases: { t: string; d: string }[];
  integrations: string[];
  audience: string[];
  proof: string;
}

export interface PillarContent {
  slug: string;
  icon: LucideIcon;
  image: string;
  accentClass: string;     // tailwind text/border tint for icon chip
  gradientClass: string;   // tailwind from→to background gradient
  glowClass: string;       // ambient blur glow
  ro: PillarLocaleContent;
  en: PillarLocaleContent;
}

export const PILLARS: PillarContent[] = [
  {
    slug: 'city',
    icon: Network,
    image: pillarCity,
    accentClass: 'text-cyan-300 border-cyan-400/40',
    gradientClass: 'from-cyan-500/30 via-sky-500/10 to-transparent',
    glowClass: 'bg-cyan-500/25',
    ro: {
      tag: 'Smart City',
      title: 'AETHERIS City OS',
      headline: 'Creierul digital al orașului tău.',
      subtitle:
        'O singură platformă care unifică traficul, mediul, parcările, iluminatul, transportul public, deșeurile și relația cu cetățeanul — cu AI predictiv pentru fiecare flux.',
      capabilities: [
        { t: 'Trafic predictiv AI', d: 'Procesare 24/7 a fluxurilor video, TomTom, HERE și senzori IoT cu modele Gemini Vision; predicții pe 15/30/60 min și recomandări de undă verde.' },
        { t: 'Parcări inteligente', d: 'Identificare automată a locurilor libere (P-001 standard), rezervare în timp real, integrare Timpark, plăți și permise digitale.' },
        { t: 'Iluminat public adaptiv', d: 'Dimming dinamic per zonă, detectare avarii, raportare consum și economii reale 35–55%.' },
        { t: 'Calitate aer & mediu', d: 'PM2.5, NO₂, O₃, zgomot, alertare populație via PWA push (RFC 8291) și dashboard live.' },
        { t: 'Cetățean digital', d: 'Tickets foto + categorie, ChatAI 24/7, informări urbanistice și răspuns automatizat AI cu validare umană.' },
        { t: 'Mobilitate AI', d: 'Camere RTSP/HLS, fallback YOLO + Gemini, contoare vehicule, biciclete, pietoni, alerte incidente.' },
      ],
      modules: ['Trafic', 'Parcări', 'Iluminat', 'Mediu', 'Deșeuri', 'Transport public', 'Cetățeni', 'Urbanism', 'CO₂'],
      kpis: [
        { v: '−42%', l: 'timp în trafic' },
        { v: '−51%', l: 'consum iluminat' },
        { v: '< 6h', l: 'rezolvare ticket cetățean' },
        { v: '99.9%', l: 'uptime senzori' },
      ],
      useCases: [
        { t: 'Primării municipale', d: 'Dashboard unic pentru primar și directori, rapoarte automate către CCR și ministere.' },
        { t: 'Aeroporturi & gări', d: 'Integrare flight feeds, parcări, taxi, mobilitate intermodală.' },
        { t: 'Zone metropolitane', d: 'Federarea mai multor primării sub o singură consolă inter-instituțională.' },
      ],
      integrations: ['TomTom', 'HERE Maps', 'Timpark', 'ANAF', 'CNAS', 'Google Maps', 'OpenWeather', 'Senzori IoT LoRaWAN'],
      audience: ['Primării', 'Consilii județene', 'Ministere', 'Regii autonome', 'Aeroporturi', 'Operatori de mobilitate'],
      proof: 'Rulează în producție în zone metropolitane cu peste 320.000 locuitori.',
    },
    en: {
      tag: 'Smart City',
      title: 'AETHERIS City OS',
      headline: 'The digital brain of your city.',
      subtitle:
        'One platform that unifies traffic, environment, parking, lighting, public transport, waste and citizen relations — with predictive AI on every flow.',
      capabilities: [
        { t: 'Predictive AI traffic', d: '24/7 ingestion of video, TomTom, HERE and IoT sensors with Gemini Vision; 15/30/60 min forecasts and green-wave recommendations.' },
        { t: 'Smart parking', d: 'Automatic free-spot detection (P-001 standard), real-time booking, Timpark integration, payments and digital permits.' },
        { t: 'Adaptive public lighting', d: 'Dynamic per-zone dimming, fault detection, consumption reports and real 35–55% savings.' },
        { t: 'Air & environment quality', d: 'PM2.5, NO₂, O₃, noise, citizen alerting via PWA push (RFC 8291) and a live dashboard.' },
        { t: 'Digital citizen', d: 'Tickets with mandatory photo + category, 24/7 ChatAI, urban planning info, automated AI responses with human validation.' },
        { t: 'AI mobility', d: 'RTSP/HLS cameras, YOLO + Gemini fallback, vehicle/bike/pedestrian counters, incident alerts.' },
      ],
      modules: ['Traffic', 'Parking', 'Lighting', 'Environment', 'Waste', 'Public transport', 'Citizens', 'Urbanism', 'CO₂'],
      kpis: [
        { v: '−42%', l: 'time in traffic' },
        { v: '−51%', l: 'lighting consumption' },
        { v: '< 6h', l: 'citizen ticket resolution' },
        { v: '99.9%', l: 'sensor uptime' },
      ],
      useCases: [
        { t: 'Municipalities', d: 'A single dashboard for the mayor and directors, automatic reports to authorities and ministries.' },
        { t: 'Airports & stations', d: 'Flight feeds, parking, taxi and intermodal mobility unified.' },
        { t: 'Metro areas', d: 'Federate multiple municipalities under one inter-institutional console.' },
      ],
      integrations: ['TomTom', 'HERE Maps', 'Timpark', 'ANAF', 'CNAS', 'Google Maps', 'OpenWeather', 'LoRaWAN IoT'],
      audience: ['City halls', 'County councils', 'Ministries', 'Public utilities', 'Airports', 'Mobility operators'],
      proof: 'Live in metropolitan areas with 320,000+ residents.',
    },
  },
  {
    slug: 'building',
    icon: Building2,
    image: pillarBuilding,
    accentClass: 'text-violet-300 border-violet-400/40',
    gradientClass: 'from-violet-500/30 via-fuchsia-500/10 to-transparent',
    glowClass: 'bg-violet-500/25',
    ro: {
      tag: 'Smart Building',
      title: 'AETHERIS Building & KNX',
      headline: 'BMS cloud-native, KNX/IP nativ, zero compromisuri.',
      subtitle:
        'Proiectare, configurare și operare a clădirilor inteligente — de la apartament la spital. Agent KNX local, dashboard cloud, energie, securitate, confort.',
      capabilities: [
        { t: 'Control KNX în timp real, fără cutii negre', d: 'Comenzile trimise din browser ajung pe magistrală în <100 ms. Telemetrie live, log audit complet și failover automat dacă agentul pierde rețeaua.' },
        { t: 'Proiectare AI a clădirii', d: 'Upload blueprint, parsare Vision AI a etajelor, scărilor și camerelor, generare automată a structurii cu AETHERIS AI.' },
        { t: 'Energy & EMS', d: 'Contoare energie, gaz, apă, energie termică, panouri solare, EV stations — totul într-un singur ecran.' },
        { t: 'Securitate & acces', d: 'Camere, control acces RBAC pe etaj/cameră, integrare alarme, tickets cu poză obligatorie.' },
        { t: 'Confort & lifestyle', d: 'Iluminat, climatizare, divertisment, scenarii de zi/noapte, asociație de proprietari, curățenie programată.' },
        { t: 'Mentenanță predictivă', d: 'Alerte AI pe consum anormal, derive de echipament, planuri preventive automate.' },
      ],
      modules: ['KNX', 'Energie', 'EMS', 'Solar', 'Gaz', 'Apă', 'Camere', 'Acces', 'Iluminat', 'Asociație proprietari', 'Curățenie'],
      kpis: [
        { v: '−38%', l: 'consum energetic' },
        { v: '< 50ms', l: 'latență KNX→UI' },
        { v: '< 24h', l: 'punere în funcțiune' },
        { v: '100%', l: 'control offline-safe' },
      ],
      useCases: [
        { t: 'Dezvoltatori imobiliari', d: 'Diferențiere premium prin smart-home livrat la cheie, brand-ul tău în PWA-ul rezidentului.' },
        { t: 'Asociații de proprietari', d: 'Gestiune apartamente, contoare individuale, plăți și anunțuri.' },
        { t: 'Spitale & clinici', d: 'Zone clinice (CLINIC, SUPPORT, ADMIN, TECH), aer comprimat, gaze medicale, energie de urgență.' },
      ],
      integrations: ['KNX/IP', 'Modbus', 'BACnet', 'Zigbee', 'Hue', 'Sonos', 'Tesla Powerwall', 'EV chargers OCPP'],
      audience: ['Dezvoltatori', 'Spitale', 'Hoteluri', 'Centre comerciale', 'Universități', 'Industrie'],
      proof: 'Implementat în spitale, hoteluri și complexuri rezidențiale active.',
    },
    en: {
      tag: 'Smart Building',
      title: 'AETHERIS Building & KNX',
      headline: 'Cloud-native BMS, native KNX/IP, zero compromises.',
      subtitle:
        'Design, commission and operate smart buildings — from one apartment to a hospital. Local KNX agent, cloud dashboard, energy, security, comfort.',
      capabilities: [
        { t: 'Real-time KNX control, no black boxes', d: 'Browser commands reach the bus in under 100 ms. Live telemetry, full audit log and automatic failover if the agent loses the network.' },
        { t: 'AI building design', d: 'Upload a blueprint, Vision AI parses floors, stairs and rooms, AETHERIS AI auto-generates the structure.' },
        { t: 'Energy & EMS', d: 'Electricity, gas, water, heat meters, solar PV, EV stations — one screen.' },
        { t: 'Security & access', d: 'Cameras, per-floor/per-room RBAC access, alarm integration, photo-mandatory tickets.' },
        { t: 'Comfort & lifestyle', d: 'Lighting, HVAC, entertainment, day/night scenes, owner association, scheduled cleaning.' },
        { t: 'Predictive maintenance', d: 'AI alerts on abnormal consumption, equipment drift, automated preventive plans.' },
      ],
      modules: ['KNX', 'Energy', 'EMS', 'Solar', 'Gas', 'Water', 'Cameras', 'Access', 'Lighting', 'Owner assoc.', 'Cleaning'],
      kpis: [
        { v: '−38%', l: 'energy consumption' },
        { v: '< 50ms', l: 'KNX→UI latency' },
        { v: '< 24h', l: 'commissioning' },
        { v: '100%', l: 'offline-safe control' },
      ],
      useCases: [
        { t: 'Real-estate developers', d: 'Premium differentiation with turn-key smart-home, your brand inside the resident PWA.' },
        { t: 'Owner associations', d: 'Apartment management, individual meters, payments and notices.' },
        { t: 'Hospitals & clinics', d: 'Clinical zones (CLINIC, SUPPORT, ADMIN, TECH), compressed air, medical gases, emergency power.' },
      ],
      integrations: ['KNX/IP', 'Modbus', 'BACnet', 'Zigbee', 'Hue', 'Sonos', 'Tesla Powerwall', 'EV chargers OCPP'],
      audience: ['Developers', 'Hospitals', 'Hotels', 'Malls', 'Universities', 'Industry'],
      proof: 'Deployed in active hospitals, hotels and residential complexes.',
    },
  },
  {
    slug: 'care',
    icon: HeartPulse,
    image: pillarCare,
    accentClass: 'text-emerald-300 border-emerald-400/40',
    gradientClass: 'from-emerald-500/30 via-teal-500/10 to-transparent',
    glowClass: 'bg-emerald-500/25',
    ro: {
      tag: 'Telemedicină',
      title: 'AETHERIS Care',
      headline: 'Telemedicină de spital, în buzunarul pacientului.',
      subtitle:
        'Suite clinic complet: Medic AI, Pacient X, consultații video criptate, dosare electronice și documente oficiale conforme cu legislația din România.',
      capabilities: [
        { t: 'Consultații video E2E', d: 'WebRTC cu TURN propriu, dictare medicală ro-RO, transcriere live, rezumat AI auto-generat.' },
        { t: 'Medic AI', d: 'Sugestii diagnostice, codificare ICD-10, retete electronice, scrisori medicale și bilete trimitere generate în secunde.' },
        { t: 'Pacient X', d: 'Dashboard pacient cu agendă, follow-up, scor de sănătate, inbox medical, waiting room virtual.' },
        { t: 'Documente clinice oficiale', d: 'PDF-uri A4 conforme: rețetă, scrisoare medicală, bilet trimitere, certificat de concediu, fără diacritice în nume fișier.' },
        { t: 'Calcul automat din CNP', d: 'Vârstă, sex, județ — direct din CNP, fără introducere manuală.' },
        { t: 'Programări inteligente', d: 'doctor_id mapat la clinical_doctors (registru independent), conflict detection, holiday-aware (sărbători RO).' },
      ],
      modules: ['Medic AI', 'Pacient X', 'Programări', 'Inbox', 'Documente', 'Specialități', 'Audit consultații', 'Disponibilitate'],
      kpis: [
        { v: '< 90s', l: 'rețetă electronică' },
        { v: '−65%', l: 'timp pe documente' },
        { v: 'E2E', l: 'criptare consultații' },
        { v: '100%', l: 'RLS pe date clinice' },
      ],
      useCases: [
        { t: 'Spitale publice', d: 'Integrare CNAS, raportare automată, audit complet al consultațiilor.' },
        { t: 'Clinici private', d: 'Brand white-label, programări online, plăți, follow-up automatizat.' },
        { t: 'Cabinete individuale', d: 'Pornire în 24h, fără infrastructură IT proprie.' },
      ],
      integrations: ['CNAS', 'ANAF', 'Resend (notificări)', 'Google Calendar', 'ElevenLabs (voce)', 'Stripe'],
      audience: ['Spitale', 'Clinici private', 'Cabinete', 'Medici de familie', 'Specialiști'],
      proof: 'Folosit zilnic de medici români din 33+ specialități clinice.',
    },
    en: {
      tag: 'Telemedicine',
      title: 'AETHERIS Care',
      headline: 'Hospital-grade telemedicine in the patient’s pocket.',
      subtitle:
        'Full clinical suite: Medic AI, Patient X, encrypted video consultations, EHR and official documents compliant with local healthcare law.',
      capabilities: [
        { t: 'E2E video consultations', d: 'WebRTC with our own TURN, medical dictation, live transcription, auto AI summary.' },
        { t: 'Medic AI', d: 'Diagnostic suggestions, ICD-10 coding, e-prescriptions, medical letters and referrals generated in seconds.' },
        { t: 'Patient X', d: 'Patient dashboard with agenda, follow-up, health score, medical inbox, virtual waiting room.' },
        { t: 'Official clinical documents', d: 'A4 compliant PDFs: prescription, medical letter, referral, sick leave — diacritic-free filenames.' },
        { t: 'Auto-derived from national ID', d: 'Age, sex, county derived directly from the personal ID — no manual entry.' },
        { t: 'Smart scheduling', d: 'doctor_id mapped to clinical_doctors registry, conflict detection, RO holiday aware.' },
      ],
      modules: ['Medic AI', 'Patient X', 'Scheduling', 'Inbox', 'Documents', 'Specialties', 'Consultation audit', 'Availability'],
      kpis: [
        { v: '< 90s', l: 'e-prescription' },
        { v: '−65%', l: 'time on paperwork' },
        { v: 'E2E', l: 'consultation crypto' },
        { v: '100%', l: 'RLS on clinical data' },
      ],
      useCases: [
        { t: 'Public hospitals', d: 'National health insurer integration, automated reporting, full consultation audit.' },
        { t: 'Private clinics', d: 'White-label brand, online booking, payments, automated follow-up.' },
        { t: 'Solo practices', d: 'Live in 24h with no in-house IT.' },
      ],
      integrations: ['National Health Insurer', 'ANAF', 'Resend', 'Google Calendar', 'ElevenLabs', 'Stripe'],
      audience: ['Hospitals', 'Private clinics', 'Practices', 'Family doctors', 'Specialists'],
      proof: 'Used daily by doctors in 33+ clinical specialties.',
    },
  },
  {
    slug: 'mind',
    icon: Brain,
    image: pillarMind,
    accentClass: 'text-rose-300 border-rose-400/40',
    gradientClass: 'from-rose-500/30 via-pink-500/10 to-transparent',
    glowClass: 'bg-rose-500/25',
    ro: {
      tag: 'Sănătate Mintală & Adicții',
      title: 'AETHERIS Mind',
      headline: 'Recuperare digitală pentru adicții comportamentale și de substanțe.',
      subtitle:
        'Suite specializat pentru joc patologic, alcool, substanțe și comportamente compulsive. CBT digital, cue-exposure, scale validate (PGSI, GSAS, DSM-5) și telemedicină 1:1.',
      capabilities: [
        { t: 'Psiholog AI hands-free', d: 'Asistent CBT continuu, microfon auto-restart ro-RO, voce ElevenLabs Alice, întreruperi naturale ale vorbirii.' },
        { t: 'Scale clinice validate', d: 'PGSI (joc), GSAS (substanțe), DSM-5 — cu rezultate stocate, evolutie pe timp și interpretare automată.' },
        { t: 'Plan clinic personalizat', d: 'Generat AI pe baza scorurilor și istoricului, validat de medic, partajat cu pacientul.' },
        { t: 'Cue-exposure controlat', d: 'Expunere graduală la stimuli declanșatori, cu monitorizare biofeedback și panică-stop.' },
        { t: 'Manual & psihoeducație', d: 'Bibliotecă structurată de materiale CBT, video, exerciții.' },
        { t: 'Rapoarte clinice', d: 'Pentru psihiatri, asistenți sociali, instanțe — PDF-uri formale pentru evaluare.' },
      ],
      modules: ['Psiholog AI', 'PGSI', 'GSAS', 'DSM-5', 'Plan clinic', 'Manual', 'Rapoarte', 'Telemedicină 1:1'],
      kpis: [
        { v: '+ 54%', l: 'aderență la program' },
        { v: '−47%', l: 'rată recidivă 6 luni' },
        { v: '24/7', l: 'asistent CBT disponibil' },
        { v: '100%', l: 'confidențialitate strictă' },
      ],
      useCases: [
        { t: 'Centre de adicții', d: 'Triaj automat, monitorizare longitudinală, raport pentru DGASPC sau instanțe.' },
        { t: 'Programe corporate EAP', d: 'Wellbeing pentru angajați, anonim, scalabil.' },
        { t: 'Pacienți individuali', d: 'Acces direct via PWA, fără stigmă, în propria limbă.' },
      ],
      integrations: ['ElevenLabs', 'Gemini 2.5 Flash', 'Google Speech ro-RO', 'Resend', 'Clinical Doctors registry'],
      audience: ['Psihiatri', 'Psihologi clinicieni', 'Centre de recuperare', 'ONG-uri', 'Companii (EAP)'],
      proof: 'Construit alături de specialiști în psihiatrie și psihologie clinică din România.',
    },
    en: {
      tag: 'Mental Health & Addictions',
      title: 'AETHERIS Mind',
      headline: 'Digital recovery for behavioural and substance addictions.',
      subtitle:
        'Specialised suite for gambling, alcohol, substances and compulsive behaviours. Digital CBT, cue-exposure, validated scales (PGSI, GSAS, DSM-5) and 1:1 telemedicine.',
      capabilities: [
        { t: 'Hands-free AI psychologist', d: 'Continuous CBT assistant, auto-restart microphone, ElevenLabs Alice voice, natural speech interruption.' },
        { t: 'Validated clinical scales', d: 'PGSI, GSAS, DSM-5 — stored results, longitudinal evolution and automated interpretation.' },
        { t: 'Personalised clinical plan', d: 'AI-generated from scores and history, validated by the clinician, shared with the patient.' },
        { t: 'Controlled cue-exposure', d: 'Graded exposure to triggering stimuli with biofeedback monitoring and panic-stop.' },
        { t: 'Manual & psychoeducation', d: 'Structured library of CBT materials, videos and exercises.' },
        { t: 'Clinical reports', d: 'For psychiatrists, social workers and courts — formal PDF assessments.' },
      ],
      modules: ['AI Psychologist', 'PGSI', 'GSAS', 'DSM-5', 'Clinical plan', 'Manual', 'Reports', '1:1 telemedicine'],
      kpis: [
        { v: '+ 54%', l: 'program adherence' },
        { v: '−47%', l: '6-month relapse rate' },
        { v: '24/7', l: 'CBT assistant on call' },
        { v: '100%', l: 'strict confidentiality' },
      ],
      useCases: [
        { t: 'Addiction centres', d: 'Automated triage, longitudinal monitoring, reports for social services or courts.' },
        { t: 'Corporate EAP', d: 'Anonymous, scalable employee wellbeing.' },
        { t: 'Individual patients', d: 'Direct PWA access, no stigma, in their own language.' },
      ],
      integrations: ['ElevenLabs', 'Gemini 2.5 Flash', 'Google Speech', 'Resend', 'Clinical Doctors registry'],
      audience: ['Psychiatrists', 'Clinical psychologists', 'Recovery centres', 'NGOs', 'Companies (EAP)'],
      proof: 'Built with Romanian psychiatry and clinical psychology specialists.',
    },
  },
  {
    slug: 'secure',
    icon: ShieldCheck,
    image: pillarSecure,
    accentClass: 'text-amber-300 border-amber-400/40',
    gradientClass: 'from-amber-500/30 via-orange-500/10 to-transparent',
    glowClass: 'bg-amber-500/25',
    ro: {
      tag: 'Cybersecurity & Conformitate',
      title: 'AETHERIS Secure',
      headline: 'Securitate de nivel clinic. Conformitate UE by design.',
      subtitle:
        'Dashboard NIS2 live, runtime pentest, GDPR și DSA integrate, MFA cu rate-limit, auditul fiecărei acțiuni — totul construit ca prim cetățean al platformei.',
      capabilities: [
        { t: 'Dashboard NIS2 live', d: 'Funcție RPC get_security_dashboard_metrics + edge function cyber-runtime-pentest care rulează scanări reale.' },
        { t: 'GDPR & DSA by design', d: 'DPIA, DSA flag, drepturi subiect, log atribuit strict pe user_id = auth.uid().' },
        { t: 'MFA + WebAuthn', d: 'TOTP, WebAuthn (biometrie), normalizare base64url, max 5 încercări per 5 minute.' },
        { t: 'Auto-logout & sesiuni', d: 'Sesiuni strict 60min, logout automat la blocare.' },
        { t: 'RLS pe tot', d: 'Row-Level Security obligatoriu pe toate tabelele sensibile, politicile strict pe authenticated.' },
        { t: 'Edge functions hardened', d: 'CSP strict, parole min. 12 caractere complexe, search_path = public, fără dangerouslySetInnerHTML.' },
      ],
      modules: ['NIS2 Dashboard', 'Pentest runtime', 'GDPR', 'DSA', 'MFA', 'WebAuthn', 'Audit log', 'IP Tracking'],
      kpis: [
        { v: '0', l: 'incidente confirmate' },
        { v: '< 200ms', l: 'API SLA' },
        { v: '< 100ms', l: 'DB SLA' },
        { v: '100%', l: 'tabele cu RLS' },
      ],
      useCases: [
        { t: 'Operatori esențiali NIS2', d: 'Mapare directă la articolele NIS2 + raportare către DNSC.' },
        { t: 'Spitale & clinici', d: 'Conformitate clinică pentru date sensibile DSGVO art. 9.' },
        { t: 'Administrație publică', d: 'Audit complet pentru CCR, AEP și DPO.' },
      ],
      integrations: ['Supabase RLS', 'WebAuthn', 'TOTP', 'Resend', 'IP geo-services', 'CSP / Permissions-Policy'],
      audience: ['CISO', 'DPO', 'IT Director', 'Operatori NIS2', 'Public sector'],
      proof: 'Toate funcțiile DB folosesc SET search_path TO public; toate bucket-urile storage forțează /{user_id}/.',
    },
    en: {
      tag: 'Cybersecurity & Compliance',
      title: 'AETHERIS Secure',
      headline: 'Clinical-grade security. EU compliance by design.',
      subtitle:
        'Live NIS2 dashboard, runtime pentest, GDPR and DSA built in, rate-limited MFA, auditing every action — first-class citizen of the platform.',
      capabilities: [
        { t: 'Live NIS2 dashboard', d: 'get_security_dashboard_metrics RPC + cyber-runtime-pentest edge function running real scans.' },
        { t: 'GDPR & DSA by design', d: 'DPIA, DSA flag, data-subject rights, logs strictly attributed via user_id = auth.uid().' },
        { t: 'MFA + WebAuthn', d: 'TOTP, WebAuthn biometrics, base64url normalisation, max 5 attempts / 5 min.' },
        { t: 'Auto-logout & sessions', d: 'Strict 60-min sessions, auto-logout on block.' },
        { t: 'RLS everywhere', d: 'Mandatory Row-Level Security on every sensitive table, policies strictly target {authenticated}.' },
        { t: 'Hardened edge functions', d: 'Strict CSP, 12-char complex passwords, search_path = public, no dangerouslySetInnerHTML.' },
      ],
      modules: ['NIS2 dashboard', 'Runtime pentest', 'GDPR', 'DSA', 'MFA', 'WebAuthn', 'Audit log', 'IP tracking'],
      kpis: [
        { v: '0', l: 'confirmed incidents' },
        { v: '< 200ms', l: 'API SLA' },
        { v: '< 100ms', l: 'DB SLA' },
        { v: '100%', l: 'tables with RLS' },
      ],
      useCases: [
        { t: 'NIS2 essential operators', d: 'Direct mapping to NIS2 articles + reporting to national CSIRTs.' },
        { t: 'Hospitals & clinics', d: 'Clinical-grade compliance for GDPR art. 9 data.' },
        { t: 'Public administration', d: 'Complete audit for auditors and DPOs.' },
      ],
      integrations: ['Supabase RLS', 'WebAuthn', 'TOTP', 'Resend', 'IP geo-services', 'CSP / Permissions-Policy'],
      audience: ['CISO', 'DPO', 'IT Director', 'NIS2 operators', 'Public sector'],
      proof: 'All DB functions use SET search_path TO public; every storage bucket enforces /{user_id}/.',
    },
  },
  {
    slug: 'intelligence',
    icon: Cpu,
    image: pillarIntelligence,
    accentClass: 'text-indigo-300 border-indigo-400/40',
    gradientClass: 'from-indigo-500/30 via-blue-500/10 to-transparent',
    glowClass: 'bg-indigo-500/25',
    ro: {
      tag: 'AI Gateway',
      title: 'AETHERIS Intelligence',
      headline: 'Modele de top, în orice modul, fără cod.',
      subtitle:
        'AI gateway unificat: Gemini 2.5 Flash și Pro, Vision AI, voce ElevenLabs și agenți specializați pe fiecare domeniu — toate orchestrate intern.',
      capabilities: [
        { t: 'AETHERIS AI', d: 'Răspunsuri rapide, context 2026, traduceri inline, sumarizări medicale, code-mode pentru parsere.' },
        { t: 'Vision AI', d: 'Analiză fotografii ticket-uri, blueprints clădiri, capturi trafic, recunoaștere semne și obiecte.' },
        { t: 'Voce ElevenLabs', d: 'Voce „Alice” ro-RO, întreruperi de vorbire, latency optimizat pentru chat continuu.' },
        { t: 'Agenți pe pagină', d: 'page-expert-chat per modul, ChatAI cetățean, Medic AI, Psiholog AI — fiecare cu prompt și RAG dedicat.' },
        { t: 'Parsere AI structurate', d: 'parse-incasare, parse-plata, parse-invoice-receptie — input liber → JSON validat.' },
        { t: 'Multilingv', d: 'translate-text edge function, persistență preferințe per utilizator.' },
      ],
      modules: ['ChatAI', 'Medic AI', 'Psiholog AI', 'Page expert', 'Parsere AI', 'Vision', 'Voce', 'Translate'],
      kpis: [
        { v: '< 1.2s', l: 'first token (Flash)' },
        { v: '99.5%', l: 'precizie parsere' },
        { v: '12+', l: 'agenți specializați' },
        { v: '100%', l: 'auditat & log-uit' },
      ],
      useCases: [
        { t: 'Operațiuni back-office', d: 'Înlocuiește încărcările manuale cu parsere AI și validare umană.' },
        { t: 'Suport cetățean', d: 'ChatAI 24/7 în limba ta, escalare la operator dacă scor < prag.' },
        { t: 'Suport clinic', d: 'Asistent AI pentru medic la patul pacientului, mâini libere.' },
      ],
      integrations: ['Gemini 2.5', 'ElevenLabs', 'Google Speech', 'Lovable AI Gateway', 'Resend'],
      audience: ['Operatori', 'Clinicieni', 'Suport clienți', 'Echipe finance', 'Echipe DevOps'],
      proof: 'Mii de inferențe pe zi în producție, cu monitorizare end-to-end.',
    },
    en: {
      tag: 'AI Gateway',
      title: 'AETHERIS Intelligence',
      headline: 'Top-tier models in every module, no code.',
      subtitle:
        'Unified AI gateway: Gemini 2.5 Flash and Pro, Vision AI, ElevenLabs voice and domain-specialised agents — all orchestrated internally.',
      capabilities: [
        { t: 'AETHERIS AI', d: 'Fast replies, 2026 context, inline translation, medical summarisation, code-mode for parsers.' },
        { t: 'Vision AI', d: 'Ticket photo analysis, building blueprints, traffic frames, sign and object recognition.' },
        { t: 'ElevenLabs voice', d: '"Alice" voice, natural speech interruption, latency-tuned for continuous chat.' },
        { t: 'Per-page agents', d: 'page-expert-chat per module, citizen ChatAI, Medic AI, AI Psychologist — each with its own prompt and RAG.' },
        { t: 'Structured AI parsers', d: 'parse-incasare, parse-plata, parse-invoice-receptie — free text → validated JSON.' },
        { t: 'Multilingual', d: 'translate-text edge function with per-user preferences.' },
      ],
      modules: ['ChatAI', 'Medic AI', 'AI Psychologist', 'Page expert', 'AI parsers', 'Vision', 'Voice', 'Translate'],
      kpis: [
        { v: '< 1.2s', l: 'first token (Flash)' },
        { v: '99.5%', l: 'parser accuracy' },
        { v: '12+', l: 'specialised agents' },
        { v: '100%', l: 'audited & logged' },
      ],
      useCases: [
        { t: 'Back-office operations', d: 'Replace manual entry with AI parsers and human validation.' },
        { t: 'Citizen support', d: '24/7 ChatAI in their language, escalation to a human if confidence < threshold.' },
        { t: 'Clinical support', d: 'Hands-free AI assistant for the doctor at the bedside.' },
      ],
      integrations: ['Gemini 2.5', 'ElevenLabs', 'Google Speech', 'Lovable AI Gateway', 'Resend'],
      audience: ['Operators', 'Clinicians', 'Customer support', 'Finance teams', 'DevOps'],
      proof: 'Thousands of inferences per day in production with end-to-end monitoring.',
    },
  },
  {
    slug: 'finance',
    icon: Wallet,
    image: pillarFinance,
    accentClass: 'text-sky-300 border-sky-400/40',
    gradientClass: 'from-sky-500/30 via-cyan-500/10 to-transparent',
    glowClass: 'bg-sky-500/25',
    ro: {
      tag: 'ERP & Finance',
      title: 'AETHERIS Finance',
      headline: 'ERP modern, în care AI face munca grea.',
      subtitle:
        'Facturare, încasări, plăți, gestiuni, NIR, retur furnizor, transferuri, KPI-uri și oferte comerciale — automatizate cu parsere AI și PDF-uri oficiale.',
      capabilities: [
        { t: 'Facturare avansată', d: 'Serii unice, auto-complete, rotunjire strictă subtotal la 2 zecimale, conversie ofertă→proforma→factură.' },
        { t: 'Încasări & plăți AI', d: 'Edge functions parse-incasare, parse-plata — încărci extras, AETHERIS structurează automat.' },
        { t: 'Stornare', d: 'Facturi storno cu valori negative și tip „storno” în DB, complet auditate.' },
        { t: 'Gestiuni & inventar', d: 'NIR-uri, transferuri, ajustări stoc, retur furnizor parțial/total bazat pe NIR.' },
        { t: 'Oferte comerciale', d: 'Parser PDF spațial pentru oferte primite, validare cu marjă 2%, generare ofertă proprie.' },
        { t: 'KPI Dashboard', d: 'Cifre cheie agregate, ordine specifică a cardurilor, drill-down pe orice indicator.' },
      ],
      modules: ['Facturi', 'Storno', 'Încasări', 'Plăți', 'NIR', 'Retur', 'Stocuri', 'Transferuri', 'Oferte', 'KPI'],
      kpis: [
        { v: '−72%', l: 'timp procesare extras' },
        { v: '99.7%', l: 'precizie parser' },
        { v: '< 5s', l: 'PDF factură A4' },
        { v: '100%', l: 'date persistate în DB' },
      ],
      useCases: [
        { t: 'IMM & retail', d: 'Operațional în 24h, fără contabil dedicat la început.' },
        { t: 'Spitale & clinici', d: 'Facturare CNAS, decontare automată, raportare lunară.' },
        { t: 'Servicii B2B', d: 'Oferte → proforma → facturi, cu citare automată a temeiului legal.' },
      ],
      integrations: ['ANAF v9', 'Stripe', 'Resend', 'Bănci (extras CSV/PDF)', 'Excel/CSV import'],
      audience: ['CFO', 'Antreprenori', 'Contabili', 'Spitale', 'Retaileri'],
      proof: 'Toate datele financiare strict în Supabase — niciodată în local storage.',
    },
    en: {
      tag: 'ERP & Finance',
      title: 'AETHERIS Finance',
      headline: 'A modern ERP where AI does the heavy lifting.',
      subtitle:
        'Invoicing, collections, payments, inventory, goods-receipt, supplier returns, transfers, KPIs and quotes — automated with AI parsers and official PDFs.',
      capabilities: [
        { t: 'Advanced invoicing', d: 'Unique series, auto-complete, strict 2-decimal subtotal rounding, quote→proforma→invoice conversion.' },
        { t: 'AI collections & payments', d: 'parse-incasare, parse-plata edge functions — upload a bank statement, AETHERIS structures it.' },
        { t: 'Storno (cancel)', d: 'Storno invoices with negative values and "storno" DB type, fully audited.' },
        { t: 'Inventory & stocks', d: 'Goods receipts, transfers, stock adjustments, partial/total supplier returns based on NIR.' },
        { t: 'Commercial offers', d: 'Spatial PDF parser for incoming offers, 2% margin validation, generate your own offer.' },
        { t: 'KPI dashboard', d: 'Aggregated key figures, specific card order, drill-down on any indicator.' },
      ],
      modules: ['Invoices', 'Storno', 'Collections', 'Payments', 'Goods receipts', 'Returns', 'Stocks', 'Transfers', 'Quotes', 'KPI'],
      kpis: [
        { v: '−72%', l: 'statement processing time' },
        { v: '99.7%', l: 'parser accuracy' },
        { v: '< 5s', l: 'A4 invoice PDF' },
        { v: '100%', l: 'data persisted to DB' },
      ],
      useCases: [
        { t: 'SME & retail', d: 'Live in 24h with no dedicated accountant at start.' },
        { t: 'Hospitals & clinics', d: 'National insurer billing, automated settlement, monthly reporting.' },
        { t: 'B2B services', d: 'Quote → proforma → invoice with automatic legal-basis citation.' },
      ],
      integrations: ['ANAF v9', 'Stripe', 'Resend', 'Banks (CSV/PDF statements)', 'Excel/CSV import'],
      audience: ['CFO', 'Founders', 'Accountants', 'Hospitals', 'Retailers'],
      proof: 'All financial data strictly in Supabase — never in local storage.',
    },
  },
];
